import { parseWithZod } from '@conform-to/zod'
import {
  type Reservation,
  type Car,
  type Payment,
  type Brand,
  type CarImage,
} from '@prisma/client'
import {
  type ActionFunctionArgs,
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
  redirect,
  replace,
} from '@remix-run/node'
import { useActionData, useLoaderData } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { CarService } from '#app/entities/car/service.server.ts'
import { ContactService } from '#app/entities/contact/service.server.ts'
import { ReservationService } from '#app/entities/reservation/service.server.ts'
import { createReturnUrl } from '#app/features/payment/createReturnUrl.ts'
import { checkAndHandlePayment } from '#app/features/payment/handlePayments.server.ts'
import { paymentErrorMessage } from '#app/features/payment/paymentErrorMessage.ts'
import { PaypalHandler } from '#app/features/payment/paypal/index.ts'
import { carIsAvailable } from '#app/routes/_api+/api.car.availability.verify.$id.ts'
import { getCarSlugMetaTags } from '#app/shared/lib/meta-tags.ts'
import { isCarOlderThan6Years } from '#app/shared/lib/misc.js'
import { invariantResponse } from '#app/shared/lib/misc.tsx'
import { calculateDiscountedPrice } from '#app/shared/lib/price.ts'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { redirectWithToast } from '#app/shared/ui/feedback/toast/toast.server.ts'
import { Button } from '#app/shared/ui/inputs/button/Button.js'
import Header from '#app/shared/ui/layout/Header.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { Dialog } from '#app/shared/ui/popups/Dialog.tsx'
import {
  type PaymentType,
  PaymentEnum,
  PaymentErrorEnum,
  TypeOfPayment,
  ContactTypeEnum,
  type TypeOfPaymentType,
  type WarrantyDurationTypes,
  WarrantyTypeEnum,
  type WarrantyTypes,
  WarrantyDurationEnum,
} from '../../../../types/enums'
import CarSummary from '../me-interesa/ui/carSummary'
import CarSummaryDrawerMobile from '../me-interesa/ui/carSummaryDrawerMobile'
import ContadoLeft from './ui/contado/contadoLeft'
import FinanciacionLeft from './ui/financiado/financiacionLeft'
import ContactPopUp from './ui/popUp/contadoPopup'
import ReservationTabs from './ui/reservationTabs'
import ReserveForm, { createReservationFormSchema } from './ui/reserveForm'
import { ElementProvider } from './ui/stripe/elementProvider'
import { useHasPackOptima } from './usePackOptima'

type ActionResponse = {
  success?: boolean
  result?: any
  status?: number
}

export const reservationCost = 600 // euros

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { carSlug } = params
  invariantResponse(carSlug, 'Car not found', { status: 400 })
  const car = (await CarService.findUnique({
    where: { slug: carSlug, isAvailable: true },
    include: {
      images: {
        select: {
          id: true,
        },
      },
      brand: true,
      reservation: {
        select: {
          isPurchaseCompleted: true,
          payment: true,
        },
      },
    },
  })) as Car & {
    images: { id: CarImage['id'] }[]
    brand: Brand
    reservation: Reservation & {
      payment: Payment | null
    }
  }
  invariantResponse(car, 'Car not found', {
    status: 404,
  })
  const url = new URL(request.url)
  const queryParams = url.searchParams

  // is payment made? if it is, handle it and return the url to redirect to / error url
  const returnUrl = await checkAndHandlePayment({
    url,
    queryParams,
    car,
    baseErrorUrl: url.pathname,
  })
  if (returnUrl) {
    return replace(returnUrl) // redirect with replace, when a car is bought, to don't show the form again if he goes backwards
  }
  const error = queryParams.get('error')
  const financingAvailable = !isCarOlderThan6Years(car)
  const typeOfPaymentURL = financingAvailable
    ? queryParams.get('typeOfPayment') || undefined
    : 'Al contado'

  if (!carIsAvailable({ car }) && !error) {
    const isSold = car.reservation?.isPurchaseCompleted
    return redirectWithToast('/nuestros-coches', {
      description: isSold
        ? 'Lo sentimos, el coche ya ha sido vendido'
        : 'Lo sentimos, el coche ya ha sido reservado',
    })
  }
  const stripePublicKey = process.env.STRIPE_PUBLIC_KEY
  invariantResponse(stripePublicKey, 'Internal server error', {
    status: 500,
  })
  return {
    car,
    stripePublicKey,
    baseUrl: request.url,
    error,
    typeOfPaymentURL,
    financingAvailable,
    domainUrl: request.url,
  }
}

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  return getCarSlugMetaTags({
    car: data?.car as any,
    domainUrl: data?.domainUrl,
    matches,
    titlePrefix: 'Reserva tu',
    index: false,
  })
}

export async function action({ params, request }: ActionFunctionArgs) {
  const { carSlug } = params
  invariantResponse(carSlug, 'Car not found', { status: 400 })

  const car = await CarService.findBySlug(carSlug)
  invariantResponse(car, 'Car not found', {
    status: 404,
  })

  // Calculate the maximum initial entry (50% of the vehicle's financing value)
  const financedPrice =
    calculateDiscountedPrice(car.minFinancedAmount ?? 0, car.discount) ?? 0
  const maxInitialEntry = Math.floor(financedPrice * 0.5)

  // Create dynamic schema with the calculated max
  const DynamicReservationFormSchema =
    createReservationFormSchema(maxInitialEntry)

  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: DynamicReservationFormSchema,
  })
  if (submission.status !== 'success') {
    return json<ActionResponse>(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const {
    email,
    fullPhone,
    name,
    postalCode,
    warrantyChoice,
    activeNumber,
    paymentType,
    initialEntry,
    carAsPayment,
  } = submission.value

  const contactResponse = await ContactService.create({
    email,
    phoneNumber: fullPhone,
    name,
    postalCode,
    type: ContactTypeEnum.BOOKING,
  })

  invariantResponse(
    contactResponse.success,
    'Failed to retrieve contact data',
    {
      status: 400,
    },
  )

  const reservationResponse = await ReservationService.create({
    contactId: contactResponse.contact.id,
    activeNumber,
    initialEntry,
    paymentType,
    warrantyChoice:
      car.year >= new Date().getFullYear() - 10 && car.mileage <= 200000
        ? {
            type: '',
            duration: 0,
          }
        : {
            type: warrantyChoice.type,
            duration: warrantyChoice.duration,
          },
    carAsPayment,
  })

  invariantResponse(
    reservationResponse.success,
    'Failed to retrieve reservation data',
    {
      status: 400,
    },
  )

  const url = new URL(request.url)
  const response: any = await new PaypalHandler().payment({
    price: reservationCost,
    currency_code: 'EUR',
    description: `Gamma Óptima, reserva del coche: ${car.brand.name}  ${car.model}`,
    // create return url => this page and validate the data in the loader
    succesUrl: createReturnUrl({
      baseUrl: url.href,
      method: PaymentEnum.PAYPAL,
      reservationId: reservationResponse.reservation.id,
    }),
    cancelUrl: url.href,
  })

  if (response.error || response.status !== 'CREATED') {
    console.error('Generic paypal error: ', response)
    return redirect(`${url.origin}?error=${PaymentErrorEnum.GENERIC}`)
  }

  // link to paypal payment
  const paypalUrlToPay = response.links[1].href

  return redirect(paypalUrlToPay)
}

export default function Index() {
  const {
    car,
    stripePublicKey,
    baseUrl,
    error,
    typeOfPaymentURL,
    financingAvailable,
  } = useLoaderData<typeof loader>()
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(Boolean(error))
  const [typeOfPayment, setTypeOfPayment] = useState<
    TypeOfPaymentType | undefined
  >(
    (financingAvailable
      ? typeOfPaymentURL
      : TypeOfPayment.AL_CONTADO) as TypeOfPaymentType,
  )
  const [paymentMethod, setPaymentMethod] = useState<PaymentType>(
    PaymentEnum.STRIPE,
  )
  const actionData = useActionData<ActionResponse>()
  const [warrantyChoice, setWarrantyChoice] = useState<{
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }>({ type: WarrantyTypeEnum.BASIC, duration: WarrantyDurationEnum.ONE_YEAR })
  const [activeNumber, setActiveNumber] = useState(72)
  const financedPrice =
    calculateDiscountedPrice(car.minFinancedAmount ?? 0, car.discount) ?? 0
  const maxPackGamaEntry = Math.floor((financedPrice ?? 0) * 0.5)
  const [initialEntry, setInitialEntry] = useState(0)

  // Calcular automáticamente si tiene Pack Óptima
  const hasPackOptima = useHasPackOptima(
    initialEntry,
    activeNumber,
    maxPackGamaEntry,
  )

  const [showContact, setShowContact] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.has('typeOfPayment')) {
      url.searchParams.delete('typeOfPayment')
      window.history.replaceState({}, '', url.pathname + url.search)
    }

    if (error) {
      url.searchParams.delete('error')
      setIsErrorModalOpen(true)
      window.history.replaceState({}, '', url.pathname + url.search)
    }
  }, [])

  const showBanner = true
  return (
    <>
      {/* HEADER */}
      <Header isSticky={true} showBanner={showBanner} />
      <CarSummaryDrawerMobile
        car={car as any}
        showBanner={showBanner}
        paymentData={{
          typeOfPayment,
          warrantyChoice,
          activeNumber,
          initialEntry,
          hasPackOptima,
          maxPackGamaEntry,
        }}
      />
      <div className="bg-neutral-25">
        <Spacer
          size="36px"
          className={`
            hidden

            lg:block
          `}
        />
        <MainContainer
          className={`
            flex flex-col gap-8 px-4

            lg:flex-row lg:justify-between

            sm:px-14

            xl:px-0
          `}
        >
          <div className={`xl:w-full`}>
            {showContact && typeOfPayment ? (
              <ElementProvider //stripe
                stripePublicKey={stripePublicKey}
                amount={reservationCost}
                purchaseDescription={`Gamma Óptima, reserva del coche: ${car.model}`}
                baseUrl={baseUrl}
                locale={'es'}
              >
                <ReserveForm
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  warrantyChoice={warrantyChoice}
                  activeNumber={activeNumber}
                  typeOfPayment={typeOfPayment}
                  initialEntry={initialEntry}
                  initialError={error}
                  submission={actionData}
                  car={car as any}
                  hasPackOptima={hasPackOptima}
                />
              </ElementProvider>
            ) : (
              <>
                {!typeOfPayment && financingAvailable && (
                  <ReservationTabs setTypeOfPayment={setTypeOfPayment} />
                )}
                {typeOfPayment === TypeOfPayment.FINANCIADO && (
                  <FinanciacionLeft
                    activeNumber={activeNumber}
                    setActiveNumber={setActiveNumber}
                    warrantyChoice={warrantyChoice}
                    setWarrantyChoice={setWarrantyChoice}
                    setShowContact={setShowContact}
                    initialEntry={initialEntry}
                    setInitialEntry={setInitialEntry}
                    setOpen={setOpen}
                    car={car as any}
                    hasPackOptima={hasPackOptima}
                    maxPackGamaEntry={maxPackGamaEntry}
                  />
                )}
                {typeOfPayment === TypeOfPayment.AL_CONTADO && (
                  <ContadoLeft
                    warrantyChoice={warrantyChoice}
                    setWarrantyChoice={setWarrantyChoice}
                    setShowContact={setShowContact}
                    setOpen={setOpen}
                    car={car as any}
                  />
                )}
              </>
            )}
          </div>
          <div
            className={`
              hidden

              lg:block
            `}
          >
            <CarSummary
              car={car as any}
              paymentData={{
                typeOfPayment,
                warrantyChoice,
                activeNumber,
                initialEntry,
                hasPackOptima,
                maxPackGamaEntry,
              }}
            />
          </div>
        </MainContainer>
        <ContactPopUp open={open} setOpen={setOpen} />
        <Spacer size="64px" />
        <Spacer size="56px" />
      </div>

      {error && (
        <Dialog
          open={isErrorModalOpen}
          onClose={() => setIsErrorModalOpen(false)}
          variant="center"
          hideIcon
          className="bg-transparent"
          overlayContent={<div className="h-full w-full bg-neutral-950/80" />}
          containerClassName="bg-white"
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-primary-600">
                  Ha ocurrido un error
                </p>
                <p className="text-lg font-bold text-neutral-900">
                  No pudimos completar la reserva
                </p>
              </div>
            </div>
            <p className="text-base-medium text-neutral-700">
              {paymentErrorMessage(error)}
            </p>
            <div className="flex justify-end">
              <Button
                variant="primary"
                className="px-5"
                onClick={() => setIsErrorModalOpen(false)}
              >
                Entendido
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}
